/**
 * Axios HTTP client for the EcoEmpower API.
 *
 * All requests go through the Vite dev-server proxy, so we use
 * relative URLs ("/api/...") rather than hard-coded ports.
 * The proxy configuration in vite.config.js routes:
 *   /api/communities/*  -> scenario-service:8002
 *   /api/datasets/*     -> dataset-service:8003
 */

import axios from 'axios';

const client = axios.create({
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000,
});

// Restore Authorization header from stored token on module load (before any React renders)
const storedToken = localStorage.getItem('ecoempower_token');
if (storedToken) {
  client.defaults.headers.common['Authorization'] = `Bearer ${storedToken}`;
}

/**
 * Response interceptor: unwrap axios response to return .data directly,
 * and normalize error messages for consistent handling in the UI.
 */
client.interceptors.response.use(
  (response) => response.data,
  (error) => {
    // On 401, clear token and redirect to login
    // Skip redirect for /api/auth/ calls (login failures, token validation)
    if (error.response?.status === 401 && !error.config?.url?.startsWith('/api/auth/')) {
      localStorage.removeItem('ecoempower_token')
      delete client.defaults.headers.common['Authorization']
      if (window.location.pathname !== '/login') {
        window.location.href = '/login'
      }
    }

    const detail = error.response?.data?.detail;
    let message;
    if (typeof detail === 'string') {
      message = detail;
    } else if (detail && typeof detail === 'object') {
      message = detail.message || JSON.stringify(detail);
    } else {
      message = error.message || 'An unexpected error occurred';
    }

    const enrichedError = new Error(message);
    enrichedError.status = error.response?.status;
    enrichedError.data = error.response?.data;
    return Promise.reject(enrichedError);
  }
);

export default client;
